import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { apiRequest, type ApiError } from "@/lib/api"
import type { Project, Workflow, WorkflowChannel } from "@/types"
import {
  Plus,
  Workflow as WorkflowIcon,
  Settings2,
  Loader2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Power,
  BarChart3,
  Copy,
  Pencil,
  Trash2,
  MoreVertical,
} from "lucide-react"
import { cn } from "@/lib/utils"

type WorkflowsResponse = {
  workflows: Workflow[]
}

type WorkflowsPageProps = {
  currentProject: Project | null
}

const WorkflowsPage = ({ currentProject }: WorkflowsPageProps) => {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const queryClient = useQueryClient()
  const [newName, setNewName] = useState("")
  const [newChannel, setNewChannel] = useState<WorkflowChannel>("push")
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [pendingWorkflowId, setPendingWorkflowId] = useState<number | null>(null)
  const [deleteWorkflowId, setDeleteWorkflowId] = useState<number | null>(null)
  const [renameWorkflow, setRenameWorkflow] = useState<{ id: number; name: string } | null>(null)

  const basePath = `/api/projects/${encodeURIComponent(projectSlug || "")}/workflows`
  const workflowsPath = `${basePath}?workflow_type=workflow`

  const { data, isLoading, isError, error } = useQuery<WorkflowsResponse, ApiError>({
    queryKey: ["workflows", projectSlug, "workflow"],
    queryFn: () => apiRequest<WorkflowsResponse>(workflowsPath),
    enabled: Boolean(projectSlug),
  })

  const workflows = data?.workflows ?? []

  const createMutation = useMutation<{ workflow: Workflow }, ApiError>({
    mutationFn: () =>
      apiRequest<{ workflow: Workflow }>(basePath, {
        method: "POST",
        body: JSON.stringify({ workflow: { name: newName.trim(), workflow_type: "workflow", channel: newChannel } }),
      }),
    onSuccess: () => {
      setNewName("")
      setNewChannel("push")
      setIsDialogOpen(false)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "workflow"] })
    },
  })

  const activateMutation = useMutation<{ workflow: Workflow }, ApiError, number>({
    mutationFn: (workflowId) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${workflowId}`, {
        method: "PATCH",
        body: JSON.stringify({ workflow: { status: "active" } }),
      }),
    onMutate: (workflowId) => {
      setPendingWorkflowId(workflowId)
    },
    onSettled: () => {
      setPendingWorkflowId(null)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "workflow"] })
    },
  })

  const stopMutation = useMutation<{ workflow: Workflow }, ApiError, number>({
    mutationFn: (workflowId) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${workflowId}/stop`, {
        method: "POST",
      }),
    onMutate: (workflowId) => {
      setPendingWorkflowId(workflowId)
    },
    onSettled: () => {
      setPendingWorkflowId(null)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "workflow"] })
    },
  })

  const duplicateMutation = useMutation<{ workflow: Workflow }, ApiError, number>({
    mutationFn: (workflowId) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${workflowId}/duplicate`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "workflow"] })
    },
  })

  const renameMutation = useMutation<{ workflow: Workflow }, ApiError, { id: number; name: string }>({
    mutationFn: ({ id, name }) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ workflow: { name } }),
      }),
    onSuccess: () => {
      setRenameWorkflow(null)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "workflow"] })
    },
  })

  const deleteMutation = useMutation<void, ApiError, number>({
    mutationFn: (workflowId) =>
      apiRequest<void>(`${basePath}/${workflowId}`, { method: "DELETE" }),
    onSuccess: () => {
      setDeleteWorkflowId(null)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "workflow"] })
    },
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) return
    createMutation.mutate()
  }

  const handleToggleStatus = (workflow: Workflow) => {
    if (workflow.status === "active") {
      stopMutation.mutate(workflow.id)
      return
    }

    activateMutation.mutate(workflow.id)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle2 className="h-4 w-4" />
      case "draft":
        return <Clock className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 border-green-200"
      case "draft":
        return "bg-gray-50 text-gray-700 border-gray-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Nunca"
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return "Nunca"
    return new Intl.DateTimeFormat("pt-BR", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  const channelLabel = (channel: WorkflowChannel) => (
    channel === "facebook_message" ? "Messenger" : "Push"
  )

  return (
    <div className="flex flex-col gap-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
          <p className="mt-1 text-base text-muted-foreground">
            Automatizações contínuas para novos inscritos
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2">
              <Plus className="h-5 w-5" />
              Novo Workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Criar Novo Workflow</DialogTitle>
                <DialogDescription>
                  Novos inscritos serão automaticamente avaliados para esta automação quando ativa.
                </DialogDescription>
              </DialogHeader>
              <div className="py-6">
                <Input
                  placeholder="ex.: Boas-vindas, Onboarding, Reativação"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  autoFocus
                  className="text-base"
                />
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Canal</p>
                  <Select value={newChannel} onValueChange={(value: WorkflowChannel) => setNewChannel(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="push">Push notification</SelectItem>
                      <SelectItem value="facebook_message">Facebook Messenger</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {createMutation.isError ? (
                  <p className="mt-2 text-sm text-destructive">{createMutation.error.message}</p>
                ) : null}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={createMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={createMutation.isPending || !newName.trim()}>
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Criar Workflow
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!currentProject ? (
        <div className="rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            Selecione um projeto para ver os workflows
          </p>
        </div>
      ) : null}

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error?.message ?? "Falha ao carregar workflows."}</p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {!isLoading && !isError && workflows.length === 0 && currentProject ? (
        <div className="rounded-lg border-2 border-dashed bg-muted/20 p-12 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <WorkflowIcon className="h-10 w-10 text-primary" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">Nenhum workflow ainda</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Crie automações contínuas para inscrever novos usuários em jornadas de mensagens.
          </p>
          <Button size="lg" className="mt-6 gap-2" onClick={() => setIsDialogOpen(true)}>
            <Plus className="h-5 w-5" />
            Criar Seu Primeiro Workflow
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && workflows.length > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {workflows.length} {workflows.length === 1 ? "workflow" : "workflows"}
            </p>
          </div>

          <div className="space-y-3">
            {workflows.map((workflow) => {
              const isPending = pendingWorkflowId === workflow.id
              const isActivating = activateMutation.isPending && isPending
              const isStopping = stopMutation.isPending && isPending

              return (
                <div
                  key={workflow.id}
                  className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/40"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold truncate">{workflow.name}</h3>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
                            getStatusColor(workflow.status)
                          )}
                        >
                          {getStatusIcon(workflow.status)}
                          <span className="capitalize">
                            {workflow.status === "active" ? "ativo" : workflow.status === "draft" ? "rascunho" : workflow.status}
                          </span>
                        </span>
                        <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium">
                          {channelLabel(workflow.channel)}
                        </span>
                      </div>

                      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span>ID: {workflow.id}</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Criado em {formatDate(workflow.created_at)}
                        </span>
                      </div>

                      <div className="sm:max-w-[140px]">
                        <div className="rounded-lg bg-blue-50 p-2 text-center">
                          <p className="text-xs text-blue-600">Inscrições</p>
                          <p className="mt-0.5 text-base font-bold text-blue-700">
                            {(workflow.enrollment_count ?? 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 md:ml-4 md:self-start">
                      <Button
                        variant={workflow.status === "active" ? "secondary" : "default"}
                        size="sm"
                        className="gap-2"
                        onClick={() => handleToggleStatus(workflow)}
                        disabled={isPending}
                      >
                        {isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {isActivating ? "Ativando..." : isStopping ? "Pausando..." : "Aguarde..."}
                          </>
                        ) : (
                          <>
                            <Power className="h-4 w-4" />
                            {workflow.status === "active" ? "Desativar" : "Ativar"}
                          </>
                        )}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="px-2">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/${projectSlug}/workflows/${workflow.id}/builder`} className="flex items-center gap-2">
                              <Settings2 className="h-4 w-4" />
                              Construtor
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/${projectSlug}/workflows/${workflow.id}/analytics`} className="flex items-center gap-2">
                              <BarChart3 className="h-4 w-4" />
                              Análises
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2"
                            onClick={() => setRenameWorkflow({ id: workflow.id, name: workflow.name })}
                          >
                            <Pencil className="h-4 w-4" />
                            Renomear
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="gap-2"
                            onClick={() => duplicateMutation.mutate(workflow.id)}
                            disabled={duplicateMutation.isPending}
                          >
                            {duplicateMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="gap-2 text-destructive focus:text-destructive"
                            onClick={() => setDeleteWorkflowId(workflow.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Deletar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : null}

      <Dialog open={renameWorkflow !== null} onOpenChange={(open) => { if (!open) setRenameWorkflow(null) }}>
        <DialogContent className="sm:max-w-[400px]">
          <form onSubmit={(e) => { e.preventDefault(); if (renameWorkflow && renameWorkflow.name.trim()) renameMutation.mutate(renameWorkflow) }}>
            <DialogHeader>
              <DialogTitle>Renomear workflow</DialogTitle>
              <DialogDescription>Informe o novo nome para este workflow.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={renameWorkflow?.name ?? ""}
                onChange={(e) => setRenameWorkflow((prev) => prev ? { ...prev, name: e.target.value } : null)}
                autoFocus
                className="text-base"
              />
              {renameMutation.isError ? (
                <p className="mt-2 text-sm text-destructive">{renameMutation.error.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameWorkflow(null)} disabled={renameMutation.isPending}>
                Cancelar
              </Button>
              <Button type="submit" disabled={renameMutation.isPending || !renameWorkflow?.name.trim()}>
                {renameMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteWorkflowId !== null} onOpenChange={(open) => { if (!open) setDeleteWorkflowId(null) }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Deletar workflow?</DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. O workflow e todos os seus dados serão removidos permanentemente.
            </DialogDescription>
          </DialogHeader>
          {deleteMutation.isError ? (
            <p className="text-sm text-destructive">{deleteMutation.error.message}</p>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteWorkflowId(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteWorkflowId !== null && deleteMutation.mutate(deleteWorkflowId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Deletar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default WorkflowsPage
